import { MenuItemConfig } from '@deltek/specification-client/core/Models/menuItemConfig'
import PopoverMenu from '@deltek/specification-client/core/Navigation/PopoverMenu'
import { MenuItem } from '@material-ui/core'
import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import React, { useMemo } from 'react'
import { useHandleCardActions } from '../hooks'
import { useShouldDisableActions } from '../hooks/useShouldDisableActions'
import { useTrackedChangesPanelStyles } from '../styles'
import { Action, TrackChangesActionsPopoverProps } from '../types/types'
import { getElementIdsForBulkActions } from '../utils/TrackChangesPanelUtils'

interface TrackChangesMenuItemConfig extends MenuItemConfig {
    disabled?: boolean
}

const ACCEPT_ALL_LABEL = 'Accept All Changes'
const REJECT_ALL_LABEL = 'Reject All Changes'
/**
 * TrackChangesActionsPopover component renders a popover menu for track changes actions.
 * It allows users to accept or reject all changes in the document.
 *
 * @param {TrackChangesActionsPopoverProps} props - The properties for the component.
 * @returns {JSX.Element} - The rendered popover menu with action items.
 */
export const TrackChangesActionsPopover = ({
    actionRef,
    isActionPopoverDisplayed,
    handleActionPopoverClick,
    transformOrigin,
}: TrackChangesActionsPopoverProps) => {
    const { handleSuggestionsInContentItem } = useHandleCardActions()
    const { actionPopover } = useTrackedChangesPanelStyles()
    const { suggestionData, trackChangesData, suggestionCardFilter } = useSuggestionsShallowSelector((state) => ({
        suggestionData: state.suggestionData,
        trackChangesData: state.trackChangesData,
        suggestionCardFilter: state.suggestionCardFilter,
    }))
    const shouldDisableActions = useShouldDisableActions()
    const suggestionIds = useMemo(
        () => getElementIdsForBulkActions(suggestionData, trackChangesData, suggestionCardFilter),
        [suggestionData, trackChangesData, suggestionCardFilter]
    )

    const trackChangesMenuItems: TrackChangesMenuItemConfig[] = useMemo(
        () => [
            {
                dataTestId: 'acceptAllChangesButtonBeta',
                label: ACCEPT_ALL_LABEL,
                disabled: shouldDisableActions,
                onClick: async () => {
                    await handleSuggestionsInContentItem(
                        suggestionIds,
                        'acceptAll' as Action
                    )
                },
            },
            {
                dataTestId: 'rejectAllChangesButtonBeta',
                label: REJECT_ALL_LABEL,
                disabled: shouldDisableActions,
                onClick: async () => {
                    await handleSuggestionsInContentItem(
                        suggestionIds,
                        'discardAll' as Action
                    )
                },
            },
        ],
        [suggestionIds, handleSuggestionsInContentItem, shouldDisableActions]
    )

    return (
        <PopoverMenu
            data-testid="trackChangesPopoverBeta"
            open={isActionPopoverDisplayed}
            anchorEl={actionRef.current}
            marginLeft={30}
            onClose={handleActionPopoverClick}
            anchorOrigin={{
                vertical: (actionRef?.current?.clientHeight ?? 0) + 85,
                horizontal: (actionRef?.current?.clientWidth ?? 0) - 10,
            }}
            transformOrigin={
                transformOrigin
                    ? transformOrigin
                    : {
                          vertical: 'bottom',
                          horizontal: 'right',
                      }
            }
        >
            {trackChangesMenuItems.map(
                (menuItem: TrackChangesMenuItemConfig) => {
                    return (
                        <MenuItem
                            key={menuItem.dataTestId}
                            data-testid={menuItem.dataTestId}
                            onClick={menuItem?.onClick as () => void}
                            className={actionPopover}
                            disabled={menuItem?.disabled}
                            title={menuItem?.label}
                            aria-label={menuItem?.label}
                        >
                            {menuItem?.label}
                        </MenuItem>
                    )
                }
            )}
        </PopoverMenu>
    )
}
